// src/components/dashboard/ApprenticeshipPanel.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import "./apprenticeshippanel.css";

/**
 * Student-only: Apprenticeship mode UI
 *
 * Rules:
 * - Submit button only becomes available when EXACT required counts are met
 * - If status is APPROVED: show photos + "APPROVED", hide upload + hide submit
 * - If status is PENDING_REVIEW: show photos + "Pending review", hide upload + hide submit
 * - If status is CHANGES_REQUESTED: show photos + "not approved..." message, allow upload + submit
 * - Delete case study button only available if NO submitted photos exist across BOTH sessions
 */

const isApprenticeshipCertificationEarned = (appr) => {
  const csArr = Array.isArray(appr?.caseStudies) ? appr.caseStudies : [];
  if (!appr?.unlocked) return false;

  // ✅ must have all 3 case studies completed
  if (csArr.length !== 3) return false;

  // ✅ every first + second session must be approved
  return csArr.every((cs) => {
    const first = String(cs?.firstSession?.status || "").toLowerCase();
    const second = String(cs?.secondSession?.status || "").toLowerCase();
    return first === "approved" && second === "approved";
  });
};

const getShotLabelsByType = (type) => {
  const t = String(type || "").toLowerCase();

  if (t === "brow") {
    return {
      before: ["Left profile (before)", "Right profile (before)", "Front full face (before)"],
      after: ["Left profile (after)", "Right profile (after)", "Front full face (after)"],
      tips: "3 Photos for Each: 1 Full Face Frontal, 1 Left Side Brow Profile, 1 Right Side Brow Profile",
    };
  }

  if (t === "lip") {
    return {
      before: ["Front full face (before)", "Lip close-up / macro lens (before)"],
      after: ["Front full face (after)", "Lip close-up / macro lens (after)"],
      tips: "2 Photos for Each: 1 Full Face & 1 Close Up (Macro Lens)",
    };
  }

  return { before: [], after: [], tips: "" };
};

const formatDateInputLabel = (value) => {
  if (!value) return "mm / dd / yyyy";
  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return "mm / dd / yyyy";
  return `${month} / ${day} / ${year}`;
};

const monthStartFromValue = (value) => {
  const [year, month] = String(value || "").split("-").map(Number);
  if (year && month) return new Date(year, month - 1, 1);
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const formatTimeInputLabel = (value, options) => {
  if (!value) return "Choose time";
  return options.find((opt) => opt.value === value)?.label || "Choose time";
};

const ApprenticeshipPanel = ({
  isOpen,
  onToggle,

  apprenticeship,
  apprenticeshipLoading,
  apprenticeshipErr,
  apprenticeshipSavingKey,

  deleteCaseStudy,

  canCreateMoreCaseStudies,
  createNextCaseStudy,

  getRequiredCounts,
  hasChangesRequested,

  // draft helpers
  getDraft,
  addFilesToDraft,
  removeDraftFile,

  // submit
  submitApprenticeshipSession,
  updateApprenticeshipSessionInfo,

  // status helpers
  prettyStatus,
  statusPillClass,
}) => {
  const toLocalSessionDraft = (value) => {
    if (!value) return { sessionDate: "", sessionTime: "" };
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return { sessionDate: "", sessionTime: "" };

    return {
      sessionDate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
      sessionTime: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
    };
  };

  const buildSessionAtValue = (draft) => {
    const sessionDate = String(draft?.sessionDate || "").trim();
    const sessionTime = String(draft?.sessionTime || "").trim();
    if (!sessionDate) return "";
    return `${sessionDate}T${sessionTime || "00:00"}`;
  };

  const sessionTimeOptions = [
    { value: "10:00", label: "10 AM" },
    { value: "11:00", label: "11 AM" },
    { value: "12:00", label: "12 PM" },
    { value: "13:00", label: "1 PM" },
    { value: "14:00", label: "2 PM" },
    { value: "15:00", label: "3 PM" },
  ];

  const formatSessionDateTime = (value) => {
    if (!value) return "—";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    const datePart = d.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const timePart = d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${datePart} at ${timePart}`;
  };
  // ===== Lightbox (INK) =====
  const [lightbox, setLightbox] = useState({
    isOpen: false,
    items: [],
    index: 0,
    title: "",
  });
  const [bookingCopied, setBookingCopied] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesConsented, setRulesConsented] = useState(false);
  const [bookingLinkOpen, setBookingLinkOpen] = useState(false);
  const caseStudyRefs = useRef([]);
  const prevCaseCountRef = useRef(0);
  const [openCaseIndex, setOpenCaseIndex] = useState(null);
  const [openSessionsByKey, setOpenSessionsByKey] = useState({});
  const [sessionInfoDrafts, setSessionInfoDrafts] = useState({});
  const [infoSavingKey, setInfoSavingKey] = useState("");
  const [editingSessionInfo, setEditingSessionInfo] = useState({});
  const [openDatePickerKey, setOpenDatePickerKey] = useState("");
  const [datePickerMonthByKey, setDatePickerMonthByKey] = useState({});
  const [openTimePickerKey, setOpenTimePickerKey] = useState("");

  const openLightbox = (items, index, title = "") => {
    const arr = Array.isArray(items) ? items.filter((x) => x?.url) : [];
    if (!arr.length) return;

    setLightbox({
      isOpen: true,
      items: arr,
      index: Math.max(0, Math.min(arr.length - 1, Number(index || 0))),
      title: title || "",
    });
  };

  const closeLightbox = () => {
    setLightbox((prev) => ({ ...prev, isOpen: false }));
  };

  const goPrev = () => {
    setLightbox((prev) => {
      const n = prev.items.length;
      if (!n) return prev;
      return { ...prev, index: (prev.index - 1 + n) % n };
    });
  };

  const goNext = () => {
    setLightbox((prev) => {
      const n = prev.items.length;
      if (!n) return prev;
      return { ...prev, index: (prev.index + 1) % n };
    });
  };

  // Lock body scroll + ESC/arrow keys (matches your INK lightbox CSS)
  useEffect(() => {
    if (!lightbox.isOpen) return;

    document.body.classList.add("modal-open");

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("modal-open");
    };
  }, [lightbox.isOpen]);

  const certificationEarned = isApprenticeshipCertificationEarned(apprenticeship);
  const isLocked = !!apprenticeship && !apprenticeship.unlocked;
  const bookingLink = String(apprenticeship?.bookingLink || "").trim();
  const consentStorageKey = useMemo(() => {
    const sid = apprenticeship?.student?._id || apprenticeship?.student?.id || "";
    return sid ? `apprenticeship_rules_consent:${sid}` : "apprenticeship_rules_consent";
  }, [apprenticeship?.student]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(consentStorageKey);
      setRulesConsented(saved === "true");
    } catch {
      setRulesConsented(false);
    }
  }, [consentStorageKey]);

  useEffect(() => {
    if (!rulesConsented) setRulesOpen(true);
    if (rulesConsented) setRulesOpen(false);
  }, [rulesConsented]);

  // If apprenticeship is re-locked, require consent again next unlock
  useEffect(() => {
    if (!apprenticeship) return;
    if (apprenticeship.unlocked) return;
    setRulesConsented(false);
    setRulesOpen(true);
    try {
      localStorage.setItem(consentStorageKey, "false");
    } catch {
      // ignore storage errors
    }
  }, [apprenticeship?.unlocked, apprenticeship]);

  const caseStudyCount = Array.isArray(apprenticeship?.caseStudies) ? apprenticeship.caseStudies.length : 0;

  useEffect(() => {
    if (!rulesConsented) {
      prevCaseCountRef.current = caseStudyCount;
      return;
    }

    const prevCount = prevCaseCountRef.current;
    if (caseStudyCount > prevCount) {
      setOpenCaseIndex(caseStudyCount - 1);
      const target = caseStudyRefs.current[caseStudyCount - 1];
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    prevCaseCountRef.current = caseStudyCount;
  }, [caseStudyCount, rulesConsented]);

  useEffect(() => {
    if (!caseStudyCount) {
      setOpenCaseIndex(null);
      return;
    }
  }, [caseStudyCount, openCaseIndex]);

  const handleConsent = () => {
    setRulesConsented(true);
    setRulesOpen(false);
    try {
      localStorage.setItem(consentStorageKey, "true");
    } catch {
      // ignore storage errors
    }
  };


  useEffect(() => {
    const csArr = Array.isArray(apprenticeship?.caseStudies) ? apprenticeship.caseStudies : [];
    const next = {};
    const editNext = {};
    csArr.forEach((cs, idx) => {
      ["first", "second"].forEach((key) => {
        const sess = key === "first" ? cs.firstSession : cs.secondSession;
        const { sessionDate, sessionTime } = toLocalSessionDraft(sess?.sessionAt);
        next[`${idx}:${key}`] = {
          clientName: String(sess?.clientName || ""),
          sessionDate,
          sessionTime,
        };
        editNext[`${idx}:${key}`] = false;
      });
    });
    setSessionInfoDrafts(next);
    setEditingSessionInfo(editNext);
    setOpenSessionsByKey({});
    setBookingLinkOpen(false);
    setOpenDatePickerKey("");
    setDatePickerMonthByKey({});
    setOpenTimePickerKey("");
  }, [apprenticeship]);

  const updateSessionDraft = (idx, key, field, value) => {
    const k = `${idx}:${key}`;
    setSessionInfoDrafts((prev) => ({
      ...prev,
      [k]: { ...(prev[k] || { clientName: "", sessionDate: "", sessionTime: "" }), [field]: value },
    }));
  };

  const shiftCalendarMonth = (pickerKey, delta) => {
    setDatePickerMonthByKey((prev) => {
      const current = prev[pickerKey] || monthStartFromValue(sessionInfoDrafts[pickerKey]?.sessionDate);
      return {
        ...prev,
        [pickerKey]: new Date(current.getFullYear(), current.getMonth() + delta, 1),
      };
    });
  };

  const selectCalendarDate = (idx, key, pickedDate) => {
    const yyyy = pickedDate.getFullYear();
    const mm = String(pickedDate.getMonth() + 1).padStart(2, "0");
    const dd = String(pickedDate.getDate()).padStart(2, "0");
    updateSessionDraft(idx, key, "sessionDate", `${yyyy}-${mm}-${dd}`);
    setOpenDatePickerKey("");
    setDatePickerMonthByKey((prev) => ({
      ...prev,
      [`${idx}:${key}`]: new Date(pickedDate.getFullYear(), pickedDate.getMonth(), 1),
    }));
  };

  const handleCopyBookingLink = async () => {
    if (!bookingLink) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(bookingLink);
      } else {
        const ta = document.createElement("textarea");
        ta.value = bookingLink;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setBookingCopied(true);
      setTimeout(() => setBookingCopied(false), 1800);
    } catch (e) {
      console.warn("Copy failed:", e);
    }
  };

  const [prepCopied, setPrepCopied] = useState(false);
  const handleCopyPrepLink = async () => {
    const prepLink = "https://studio.cosmetink.com/preparation";
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(prepLink);
      } else {
        const ta = document.createElement("textarea");
        ta.value = prepLink;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setPrepCopied(true);
      setTimeout(() => setPrepCopied(false), 1800);
    } catch (e) {
      console.warn("Copy failed:", e);
    }
  };

  return (
    <div className="dash-panel">
      {/* ✅ Collapsible header */}
      <button type="button" className="dash-accordion-head" onClick={onToggle}>
        <div className="dash-accordion-title">
          <h2 className="dash-panel-title" style={{ margin: 0 }}>
            Apprenticeship Mode
            {isLocked && (
              <span
                className="dash-apprenticeship-lock-pill"
                title="Locked — your master will unlock this when you're ready"
              >
                🔒 Locked
              </span>
            )}
            {!isLocked && !rulesConsented && (
              <span className="dash-apprenticeship-changes-pill" title="Consent required">
                <i />
                Consent Required
              </span>
            )}

            {hasChangesRequested && (
              <span className="dash-apprenticeship-changes-pill" title="Changes requested">
                <i />
                Changes Requested
              </span>
            )}
          </h2>

          <p className="dash-panel-subtitle" style={{ margin: "6px 0 0" }}>
            Complete 3 case studies (first + second session) to earn a 100-hr Certification.
            <br />
            Upload images, get reviewed, and track approvals.
          </p>

          {certificationEarned && (
            <div className="dash-apprenticeship-earned">
              <span className="dash-apprenticeship-earned-badge">🏅 100-hr Certification Earned</span>
            </div>
          )}
        </div>

        <span className={`dash-accordion-chevron ${isOpen ? "is-open" : ""}`} aria-hidden="true">
          ▾
        </span>
      </button>

      {/* ✅ Collapsible body */}
      {isOpen && (
        <div className="dash-accordion-body">
          {apprenticeshipLoading ? (
            <div className="dash-course-note">Loading apprenticeship…</div>
          ) : apprenticeshipErr ? (
            <div className="dash-course-note">{apprenticeshipErr}</div>
          ) : !apprenticeship ? (
            <div className="dash-course-note">No apprenticeship record yet.</div>
          ) : !apprenticeship.unlocked ? (
            <div className="dash-course-note">Locked. Your master will unlock this when you’re ready.</div>
          ) : (
            <>
              <div className="dash-apprenticeship-wrap">
                {!rulesConsented && (
                  <div className="dash-course-note">
                    Consent is required to unlock Apprenticeship Mode content.
                  </div>
                )}
                <div className="dash-apprenticeship-rules">
                  <button
                    type="button"
                    className="dash-apprenticeship-rules-toggle"
                    onClick={() => setRulesOpen((v) => !v)}
                    aria-expanded={rulesOpen}
                  >
                    <span className="dash-apprenticeship-rules-toggle-left">
                      <span className="dash-apprenticeship-rules-toggle-title">
                        General Rules and Information for Apprenticeship
                      </span>
                      {rulesConsented && (
                        <span className="dash-apprenticeship-rules-toggle-sub">
                          <span className="dash-apprenticeship-consent-dot" aria-hidden="true" />
                          <span>Consent recorded</span>
                        </span>
                      )}
                    </span>
                    <span className={`dash-apprenticeship-toggle-caret ${rulesOpen ? "is-open" : ""}`}>
                      {rulesOpen ? "−" : "+"}
                    </span>
                  </button>

                  {rulesOpen && (
                    <div className="dash-apprenticeship-rules-body">
                      <ul>
                        <li>
                          There is a $150 room block/model fee that is paid directly by the model or student to
                          COSMETINK using the link.
                        </li>
                      <li>
                        When choosing a date/time with your client, please make sure you mutually agree on the date/time
                        before instructing your clients to book. Dates and times on the link are provided during our
                        business hours and when our master artists are in the studio.
                      </li>
                        <li>
                          You are responsible for advising clients how to prepare for their appointment.
                        </li>
                      <li>
                        Clients will fill out forms in the studio, which we will provide. Artists are responsible for
                        bringing their kits with all supplies needed to perform the procedure on their models (machine,
                        needles, water wipes, pigments, etc.). <strong>If student does not have supplies they will be asked to reschedule their model.</strong>
                      </li>
                      <li>
                        Trainers and Master Artists in our studio will be available for support, and we encourage you to
                        ask us questions so we can help, but we will not be permitted to do any work for you on your
                        models. Please be well-practiced so you are confident and ready to do the work yourself. After
                        all, we will not be there once you are off to the real client world.
                      </li>
                      <li>
                        If you are still overly struggling with your models' brow mapping, we may ask that you
                        reschedule your model and send you home to practice more and submit more practice work. You can
                        review the mapping videos and mapping worksheets with your latex model again. Also, mapping
                        friends or spouses can help :)
                      </li>
                      <li>
                        If you have started your model and are performing insufficient work, we reserve the right to ask
                        you to immediately discontinue working on your model and send you home for more practice.
                      </li>
                      <li>
                        Submitted case studies need to be APPROVED in order to pass requirements and obtain your
                        official certification. This means if the work done to the model was not up to industry
                        standards we can not approve this model.
                      </li>
                      <li>
                        Models must not have any previous work. We only recommend working on previous work after gaining
                        more experience and seeing a success-rate on your healed work. If your model shows up with
                        previous work, they will be refunded and cancelled. Be sure to confirm with them beforehand.
                      </li>
                      <li>
                        Please do not use any other methods other than our COSMETINK techniques. Since the work is done
                        using our studio, we will not allow you to use any other methods of cosmetic tattooing, as you
                        are under our care, and any work done is a liability for us. Doing so will result in forfeiting
                        your apprenticeship.
                      </li>
                      <li>
                        While you can accept tips from your models, you may not charge additional fees for your service.
                        If we discover that you are charging additional fees, you will forfeit your option to
                        participate.
                      </li>
                      <li>
                        NOTE: Please allow for up to 7 business days for us to review your case studies once submitted.
                      </li>
                      </ul>

                    </div>
                  )}
                </div>

                {!rulesConsented && (
                  <div className="dash-apprenticeship-consent">
                    <div className="dash-course-note">
                      Please read the rules above. You must consent before you can access booking and case studies.
                    </div>
                    <button type="button" className="dash-chip-button" onClick={handleConsent}>
                      I have read and agree to follow the program rules
                    </button>
                  </div>
                )}
                {rulesConsented && (
                  <div className="dash-apprenticeship-link-card">
                    <button
                      type="button"
                      className="dash-apprenticeship-link-header dash-apprenticeship-link-toggle"
                      onClick={() => setBookingLinkOpen((v) => !v)}
                      aria-expanded={bookingLinkOpen}
                    >
                      <div className="dash-course-meta dash-apprenticeship-link-title">
                        <span>Your Booking Link</span>
                      </div>
                      <span className="dash-apprenticeship-link-header-right">
                        <span className={`dash-apprenticeship-toggle-caret ${bookingLinkOpen ? "is-open" : ""}`}>
                          {bookingLinkOpen ? "−" : "+"}
                        </span>
                      </span>
                    </button>
                    {bookingLinkOpen && (
                      <>
                        <ul className="dash-apprenticeship-link-checklist">
                          <li>Confirm the date and time with your model before booking.</li>
                          <li>Send the link so your model can book and pay.</li>
                          <li>Note the date and time for yourself and keep it in your case studies below for records.</li>
                        </ul>
                        <div className="dash-apprenticeship-booking-callout">
                          <div className="dash-apprenticeship-booking-title">Model Booking Link</div>
                          <div className="dash-apprenticeship-link-row">
                            {bookingLink ? (
                              <a
                                href={bookingLink}
                                target="_blank"
                                rel="noreferrer"
                                className="dash-apprenticeship-link"
                                title={bookingLink}
                              >
                                {bookingLink}
                              </a>
                            ) : (
                              <span className="dash-apprenticeship-link dash-apprenticeship-link--placeholder">
                                Booking link will appear here once your master adds it.
                              </span>
                            )}
                            <button
                              type="button"
                              className={`dash-chip-button dash-apprenticeship-copy-btn ${
                                bookingCopied ? "is-copied" : ""
                              }`}
                              onClick={handleCopyBookingLink}
                              disabled={!bookingLink}
                            >
                              {bookingCopied ? "Copied!" : "Copy link"}
                            </button>
                          </div>
                        </div>
                        <div className="dash-apprenticeship-prepare-callout">
                          <div className="dash-apprenticeship-prepare-title">Precare for Models — Important</div>
                          <div className="dash-apprenticeship-prepare-copy">
                            Send this to your model for proper preparation:
                          </div>
                          <div className="dash-apprenticeship-prepare-row">
                            <a
                              href="https://studio.cosmetink.com/preparation"
                              target="_blank"
                              rel="noreferrer"
                              className="dash-apprenticeship-prepare-link"
                            >
                              https://studio.cosmetink.com/preparation
                            </a>
                            <button
                              type="button"
                              className={`dash-chip-button dash-apprenticeship-copy-btn ${
                                prepCopied ? "is-copied" : ""
                              }`}
                              onClick={handleCopyPrepLink}
                            >
                              {prepCopied ? "Copied!" : "Copy link"}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Create next case study (until 3) */}
                {rulesConsented && canCreateMoreCaseStudies && (
                  <div className="dash-apprenticeship-actions">
                    <div className="dash-course-meta">Create your next case study (up to 3 total):</div>

                  <div className="dash-apprenticeship-create-row">
                    <button
                      type="button"
                      className="dash-chip-button"
                      disabled={apprenticeshipSavingKey === "create-case"}
                      onClick={() => createNextCaseStudy("brow")}
                    >
                      + Brow case study
                    </button>
                    <button
                      type="button"
                      className="dash-chip-button dash-chip-button-secondary"
                      disabled={apprenticeshipSavingKey === "create-case"}
                      onClick={() => createNextCaseStudy("lip")}
                    >
                      + Lip case study
                    </button>
                  </div>
                </div>
                )}

                {/* Case studies list */}
                {rulesConsented && (apprenticeship.caseStudies || []).length === 0 ? (
                  <div className="dash-course-note">You’re unlocked ✅ Create your first case study above to begin.</div>
                ) : null}
                {rulesConsented &&
                  (apprenticeship.caseStudies || []).map((cs, idx) => {
                    const type = String(cs.type || "").toLowerCase();
                    const req = getRequiredCounts(type);
                    const sessions = [
                      { key: "first", label: "First session" },
                      { key: "second", label: "Second session" },
                    ];
                    const firstPhotos = Array.isArray(cs?.firstSession?.photos) ? cs.firstSession.photos : [];
                    const secondPhotos = Array.isArray(cs?.secondSession?.photos) ? cs.secondSession.photos : [];
                    const hasAnySubmittedPhotos = firstPhotos.length > 0 || secondPhotos.length > 0;
                    const canDeleteCaseStudy = !hasAnySubmittedPhotos;
                    const shotLabels = getShotLabelsByType(type);
                    const isOpen = openCaseIndex === idx;

                    return (
                      <div
                        key={idx}
                        ref={(el) => {
                          caseStudyRefs.current[idx] = el;
                        }}
                        className="dash-course-card dash-apprenticeship-case"
                      >
                        <div
                          className="dash-apprenticeship-case-head"
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                          const nextIndex = isOpen ? null : idx;
                          setOpenCaseIndex(nextIndex);
                            if (nextIndex !== null) {
                              requestAnimationFrame(() => {
                                const target = caseStudyRefs.current[nextIndex];
                                if (target && typeof target.scrollIntoView === "function") {
                                  target.scrollIntoView({ behavior: "smooth", block: "start" });
                                }
                              });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              const nextIndex = isOpen ? null : idx;
                              setOpenCaseIndex(nextIndex);
                              if (nextIndex !== null) {
                                requestAnimationFrame(() => {
                                  const target = caseStudyRefs.current[nextIndex];
                                  if (target && typeof target.scrollIntoView === "function") {
                                    target.scrollIntoView({ behavior: "smooth", block: "start" });
                                  }
                                });
                              }
                            }
                          }}
                        aria-expanded={isOpen}
                        style={{ cursor: "pointer", width: "100%" }}
                      >
                        <div className="dash-course-title">
                          {isOpen ? "▾" : "▸"} Case Study {idx + 1} —{" "}
                          {type === "brow" ? "Brow" : type === "lip" ? "Lip" : "Case"}
                        </div>

                          {canDeleteCaseStudy && (
                            <button
                              type="button"
                              className="dash-apprenticeship-delete"
                              title="Remove this case study"
                              onClick={(e) => {
                                e.stopPropagation();
                                const ok = window.confirm(
                                  "Remove this case study? This will allow you to choose again."
                                );
                                if (ok) deleteCaseStudy(idx);
                              }}
                              disabled={apprenticeshipSavingKey === `delete-case:${idx}`}
                            >
                              ✕
                            </button>
                          )}
                        </div>

                        {isOpen && (
                          <>
                            <div className="dash-course-meta">
                              Required each session:{" "}
                              <span className="dash-course-meta-strong">{req.before} BEFORE</span> +{" "}
                              <span className="dash-course-meta-strong">{req.after} AFTER</span> images
                            </div>

                            {shotLabels.tips ? (
                              <div className="dash-course-note" style={{ marginTop: 8 }}>
                                {shotLabels.tips}
                              </div>
                            ) : null}

                            <div className="dash-apprenticeship-sessions">
                              {sessions.map((s) => {
                                const sessObj = s.key === "first" ? cs.firstSession : cs.secondSession;
                                const sessionOpenKey = `${idx}:${s.key}`;
                                const isSessionOpen = !!openSessionsByKey[sessionOpenKey];
                                const statusRaw = sessObj?.status || "not_submitted";
                                const status = String(statusRaw || "").toLowerCase();
                                const notes = sessObj?.reviewerNotes || "";
                                const isApproved = status === "approved";
                                const isPending = status === "pending_review";
                                const isChanges = status === "changes_requested";
                                const canInteract = !isApproved && !isPending;
                                const draft = getDraft(idx, s.key) || {};
                                const beforeDraft = Array.isArray(draft.before) ? draft.before : [];
                                const afterDraft = Array.isArray(draft.after) ? draft.after : [];
                                const draftReady = beforeDraft.length === req.before && afterDraft.length === req.after;
                                const savingThis = apprenticeshipSavingKey === `${idx}:${s.key}`;
                                const submitDisabled = savingThis || !canInteract || !draftReady;

                                let submitTitle = "Submit images for review";
                                if (!canInteract) submitTitle = isApproved ? "Approved" : "Pending review";
                                else if (!draftReady) {
                                  submitTitle = `Upload exactly ${req.before} BEFORE and ${req.after} AFTER photos first`;
                                }

                                const infoKey = `${idx}:${s.key}`;
                                const infoDraft = sessionInfoDrafts[infoKey] || {
                                  clientName: "",
                                  sessionDate: "",
                                  sessionTime: "",
                                };
                                const infoSaving = infoSavingKey === infoKey;
                                const hasSavedInfo = Boolean(
                                  String(sessObj?.clientName || "").trim() || sessObj?.sessionAt
                                );
                                const canEditInfo = !isPending && !isApproved;
                                const isEditing = canEditInfo && (editingSessionInfo[infoKey] || !hasSavedInfo);
                                const sessPhotos = Array.isArray(sessObj?.photos) ? sessObj.photos : [];
                                const lightboxItems = sessPhotos.map((x, j) => {
                                  const kind = String(x?.kind || "").toLowerCase();
                                  const isBefore = kind === "before";
                                  const slotIndex = sessPhotos
                                    .slice(0, j)
                                    .filter((p0) => String(p0?.kind || "").toLowerCase() === kind).length;
                                  const slotLabel = isBefore
                                    ? shotLabels?.before?.[slotIndex] || `Before ${slotIndex + 1}`
                                    : shotLabels?.after?.[slotIndex] || `After ${slotIndex + 1}`;
                                  const sessionLabel = s?.label || "";
                                  const caseLabel = type === "brow" ? "Brow" : type === "lip" ? "Lip" : "Case";

                                  return {
                                    url: x?.url,
                                    label: `${caseLabel} · ${sessionLabel} · ${slotLabel}`,
                                  };
                                });

                                return (
                                  <div key={s.key} className="dash-apprenticeship-session">
                                    <button
                                      type="button"
                                      className="dash-apprenticeship-session-head dash-apprenticeship-session-toggle"
                                      onClick={() =>
                                        setOpenSessionsByKey((prev) => ({
                                          ...prev,
                                          [sessionOpenKey]: !prev[sessionOpenKey],
                                        }))
                                      }
                                    aria-expanded={isSessionOpen}
                                  >
                                    <div className="dash-course-meta">
                                      <span className="dash-course-meta-strong">
                                        {isSessionOpen ? "▾" : "▸"} {s.label}
                                      </span>{" "}
                                      <span className={statusPillClass(status)} style={{ marginLeft: 8 }}>
                                        {prettyStatus(status)}
                                        </span>
                                      </div>
                                    </button>

                                    {isSessionOpen && (
                                      <>
                                        <div className="dash-apprenticeship-session-info">
                                          <div className="dash-course-meta">Session details</div>
                                          {isEditing ? (
                                            <div className="dash-apprenticeship-info-grid">
                                              {(() => {
                                                const pickerKey = `${idx}:${s.key}`;
                                                const viewMonth =
                                                  datePickerMonthByKey[pickerKey] ||
                                                  monthStartFromValue(infoDraft.sessionDate);
                                                const monthLabel = viewMonth.toLocaleDateString(undefined, {
                                                  month: "long",
                                                  year: "numeric",
                                                });
                                                const firstDay = new Date(
                                                  viewMonth.getFullYear(),
                                                  viewMonth.getMonth(),
                                                  1
                                                );
                                                const firstWeekday = firstDay.getDay();
                                                const daysInMonth = new Date(
                                                  viewMonth.getFullYear(),
                                                  viewMonth.getMonth() + 1,
                                                  0
                                                ).getDate();
                                                const slots = [];
                                                for (let i = 0; i < firstWeekday; i += 1) slots.push(null);
                                                for (let day = 1; day <= daysInMonth; day += 1) {
                                                  slots.push(
                                                    new Date(
                                                      viewMonth.getFullYear(),
                                                      viewMonth.getMonth(),
                                                      day
                                                    )
                                                  );
                                                }

                                                return (
                                                  <>
                                              <input
                                                type="text"
                                                className="dash-apprenticeship-info-input"
                                                placeholder="Client name"
                                                value={infoDraft.clientName}
                                                onChange={(e) =>
                                                  updateSessionDraft(idx, s.key, "clientName", e.target.value)
                                                }
                                              />
                                              <div className="dash-apprenticeship-date-wrap">
                                                <button
                                                  type="button"
                                                  className="dash-apprenticeship-info-input dash-apprenticeship-date-trigger"
                                                  onClick={() => {
                                                    setDatePickerMonthByKey((prev) => ({
                                                      ...prev,
                                                      [pickerKey]:
                                                        prev[pickerKey] ||
                                                        monthStartFromValue(infoDraft.sessionDate),
                                                    }));
                                                    setOpenDatePickerKey((prev) =>
                                                      prev === pickerKey ? "" : pickerKey
                                                    );
                                                    setOpenTimePickerKey("");
                                                  }}
                                                >
                                                  <span>{formatDateInputLabel(infoDraft.sessionDate)}</span>
                                                  <span className="dash-apprenticeship-date-trigger-icon">▾</span>
                                                </button>

                                                {openDatePickerKey === pickerKey && (
                                                  <div className="dash-apprenticeship-date-popover">
                                                    <div className="dash-apprenticeship-date-popover-head">
                                                      <button
                                                        type="button"
                                                        className="dash-apprenticeship-date-nav"
                                                        onClick={() => shiftCalendarMonth(pickerKey, -1)}
                                                      >
                                                        ‹
                                                      </button>
                                                      <div className="dash-apprenticeship-date-month">
                                                        {monthLabel}
                                                      </div>
                                                      <button
                                                        type="button"
                                                        className="dash-apprenticeship-date-nav"
                                                        onClick={() => shiftCalendarMonth(pickerKey, 1)}
                                                      >
                                                        ›
                                                      </button>
                                                    </div>
                                                    <div className="dash-apprenticeship-date-weekdays">
                                                      {["S", "M", "T", "W", "T", "F", "S"].map((day, dayIdx) => (
                                                        <span key={`${day}-${dayIdx}`}>{day}</span>
                                                      ))}
                                                    </div>
                                                    <div className="dash-apprenticeship-date-grid">
                                                      {slots.map((slot, slotIdx) => {
                                                        if (!slot) {
                                                          return (
                                                            <span
                                                              key={`empty-${slotIdx}`}
                                                              className="dash-apprenticeship-date-empty"
                                                            />
                                                          );
                                                        }

                                                        const slotValue = `${slot.getFullYear()}-${String(
                                                          slot.getMonth() + 1
                                                        ).padStart(2, "0")}-${String(slot.getDate()).padStart(
                                                          2,
                                                          "0"
                                                        )}`;
                                                        const isSelected = slotValue === infoDraft.sessionDate;

                                                        return (
                                                          <button
                                                            key={slotValue}
                                                            type="button"
                                                            className={[
                                                              "dash-apprenticeship-date-day",
                                                              isSelected
                                                                ? "dash-apprenticeship-date-day-selected"
                                                                : "",
                                                            ]
                                                              .filter(Boolean)
                                                              .join(" ")}
                                                            onClick={() =>
                                                              selectCalendarDate(idx, s.key, slot)
                                                            }
                                                          >
                                                            {slot.getDate()}
                                                          </button>
                                                        );
                                                      })}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="dash-apprenticeship-select-wrap">
                                                <button
                                                  type="button"
                                                  className="dash-apprenticeship-info-input dash-apprenticeship-date-trigger"
                                                  onClick={() => {
                                                    setOpenTimePickerKey((prev) =>
                                                      prev === pickerKey ? "" : pickerKey
                                                    );
                                                    setOpenDatePickerKey("");
                                                  }}
                                                >
                                                  <span>
                                                    {formatTimeInputLabel(infoDraft.sessionTime, sessionTimeOptions)}
                                                  </span>
                                                  <span className="dash-apprenticeship-date-trigger-icon">▾</span>
                                                </button>
                                                {openTimePickerKey === pickerKey && (
                                                  <div className="dash-apprenticeship-time-popover">
                                                    {sessionTimeOptions.map((opt) => (
                                                      <button
                                                        key={opt.value}
                                                        type="button"
                                                        className={[
                                                          "dash-apprenticeship-time-option",
                                                          infoDraft.sessionTime === opt.value
                                                            ? "is-selected"
                                                            : "",
                                                        ].join(" ")}
                                                        onClick={() => {
                                                          updateSessionDraft(
                                                            idx,
                                                            s.key,
                                                            "sessionTime",
                                                            opt.value
                                                          );
                                                          setOpenTimePickerKey("");
                                                        }}
                                                      >
                                                        {opt.label}
                                                      </button>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                              <button
                                                type="button"
                                                className="dash-chip-button"
                                                disabled={infoSaving}
                                                onClick={async () => {
                                                  try {
                                                    setInfoSavingKey(infoKey);
                                                    await updateApprenticeshipSessionInfo({
                                                      index: idx,
                                                      session: s.key,
                                                      clientName: infoDraft.clientName,
                                                      sessionAt: buildSessionAtValue(infoDraft),
                                                    });
                                                  } finally {
                                                    setInfoSavingKey("");
                                                    setEditingSessionInfo((prev) => ({ ...prev, [infoKey]: false }));
                                                  }
                                                }}
                                              >
                                                {infoSaving ? "Saving…" : "Save session info"}
                                              </button>
                                                  </>
                                                );
                                              })()}
                                            </div>
                                          ) : (
                                            <div className="dash-apprenticeship-info-text">
                                              <div>
                                                <span className="dash-course-meta-strong">Client:</span>{" "}
                                                {sessObj?.clientName || "—"}
                                              </div>
                                              <div>
                                                <span className="dash-course-meta-strong">Date/Time:</span>{" "}
                                                {formatSessionDateTime(sessObj?.sessionAt)}
                                              </div>
                                              {canEditInfo && (
                                                <button
                                                  type="button"
                                                  className="dash-chip-button dash-chip-button-secondary"
                                                  onClick={() =>
                                                    setEditingSessionInfo((prev) => ({ ...prev, [infoKey]: true }))
                                                  }
                                                >
                                                  Update session info
                                                </button>
                                              )}
                                            </div>
                                          )}
                                        </div>

                                        {isChanges && (
                                          <div className="dash-course-note dash-apprenticeship-changes">
                                            {notes ? (
                                              <div className="dash-apprenticeship-notes">
                                                <span className="dash-course-meta-strong">Instructor notes:</span> {notes}
                                              </div>
                                            ) : null}
                                          </div>
                                        )}

                                        {sessPhotos.length > 0 && (
                                          <div className="dash-apprenticeship-submitted">
                                            <div className="dash-course-meta dash-apprenticeship-submitted-title">
                                              Submitted photos
                                            </div>

                                            <div className="dash-apprenticeship-photo-grid">
                                              {sessPhotos.map((p, i) => (
                                                <button
                                                  key={i}
                                                  type="button"
                                                  className="dash-apprenticeship-photo"
                                                  title={lightboxItems[i]?.label || p.kind}
                                                  onClick={() =>
                                                    openLightbox(lightboxItems, i, `Case Study ${idx + 1} · ${s.label}`)
                                                  }
                                                >
                                                  <img
                                                    src={p.url}
                                                    alt={lightboxItems[i]?.label || p.kind || "Photo"}
                                                  />
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        )}

                                        {canInteract && (
                                          <>
                                            <div className="dash-apprenticeship-upload-grid">
                                              <div className="dash-apprenticeship-uploader">
                                                <div className="dash-course-meta dash-apprenticeship-count">
                                                  BEFORE ({beforeDraft.length}/{req.before})
                                                </div>

                                                <input
                                                  className="dash-file-input"
                                                  type="file"
                                                  accept="image/*"
                                                  multiple
                                                  onChange={(e) => {
                                                    addFilesToDraft(idx, s.key, "before", e.target.files);
                                                    e.target.value = "";
                                                  }}
                                                />

                                                {beforeDraft.length > 0 && (
                                                  <div className="dash-photo-grid">
                                                    {beforeDraft.map((f, i) => {
                                                      const url = URL.createObjectURL(f);
                                                      const label = shotLabels.before?.[i] || `Before ${i + 1}`;

                                                      return (
                                                        <div key={i} className="dash-photo-tile">
                                                          <img
                                                            src={url}
                                                            onLoad={() => URL.revokeObjectURL(url)}
                                                            alt={label}
                                                            className="dash-photo-img"
                                                          />

                                                          <div className="dash-photo-slot-label">{label}</div>

                                                          <button
                                                            type="button"
                                                            className="dash-photo-remove"
                                                            onClick={() => removeDraftFile(idx, s.key, "before", i)}
                                                            title="Remove"
                                                          >
                                                            ✕
                                                          </button>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </div>

                                              <div className="dash-apprenticeship-uploader">
                                                <div className="dash-course-meta dash-apprenticeship-count">
                                                  AFTER ({afterDraft.length}/{req.after})
                                                </div>

                                                <input
                                                  className="dash-file-input"
                                                  type="file"
                                                  accept="image/*"
                                                  multiple
                                                  onChange={(e) => {
                                                    addFilesToDraft(idx, s.key, "after", e.target.files);
                                                    e.target.value = "";
                                                  }}
                                                />

                                                {afterDraft.length > 0 && (
                                                  <div className="dash-photo-grid">
                                                    {afterDraft.map((f, i) => {
                                                      const url = URL.createObjectURL(f);
                                                      const label = shotLabels.after?.[i] || `After ${i + 1}`;

                                                      return (
                                                        <div key={i} className="dash-photo-tile">
                                                          <img
                                                            src={url}
                                                            onLoad={() => URL.revokeObjectURL(url)}
                                                            alt={label}
                                                            className="dash-photo-img"
                                                          />

                                                          <div className="dash-photo-slot-label">{label}</div>

                                                          <button
                                                            type="button"
                                                            className="dash-photo-remove"
                                                            onClick={() => removeDraftFile(idx, s.key, "after", i)}
                                                            title="Remove"
                                                          >
                                                            ✕
                                                          </button>
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                )}
                                              </div>
                                            </div>

                                            <div className="dash-course-note dash-apprenticeship-tip">
                                              {draftReady ? (
                                                <>
                                                  ✅ Ready to submit. Click{" "}
                                                  <span className="dash-course-meta-strong">Submit for review</span>.
                                                </>
                                              ) : (
                                                <>
                                                  Upload exactly{" "}
                                                  <span className="dash-course-meta-strong">{req.before} BEFORE</span> +{" "}
                                                  <span className="dash-course-meta-strong">{req.after} AFTER</span>{" "}
                                                  photos to enable submission.
                                                </>
                                              )}
                                            </div>
                                          </>
                                        )}

                                        {!isApproved && !isPending && (
                                          <div className="dash-apprenticeship-submit-row">
                                            <button
                                              type="button"
                                              className="dash-chip-button"
                                              onClick={() => submitApprenticeshipSession({ index: idx, session: s.key })}
                                              disabled={submitDisabled}
                                              title={submitTitle}
                                            >
                                              {savingThis
                                                ? "Submitting…"
                                                : draftReady
                                                ? "Submit for review"
                                                : "Add required photos"}
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ✅ Lightbox (uses your existing INK CSS classes) */}
      {lightbox.isOpen && (
        <div
          className="ink-lightbox-backdrop"
          onMouseDown={(e) => {
            // close only if clicking backdrop (not the image/card)
            if (e.target === e.currentTarget) closeLightbox();
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="ink-lightbox-card" onMouseDown={(e) => e.stopPropagation()}>
            <div className="ink-lightbox-head">
              <div className="ink-lightbox-title">
                {lightbox.title || "Photo"}
                {lightbox.items.length > 1 ? (
                  <span className="ink-lightbox-count">
                    {lightbox.index + 1}/{lightbox.items.length}
                  </span>
                ) : null}
              </div>

              <button type="button" className="ink-lightbox-close" onClick={closeLightbox} aria-label="Close">
                ✕
              </button>
            </div>

            <div className="ink-lightbox-body">
              <button
                type="button"
                className="ink-lightbox-nav ink-lightbox-prev"
                onClick={goPrev}
                disabled={lightbox.items.length <= 1}
                aria-label="Previous"
              >
                ‹
              </button>

              <img
                className="ink-lightbox-img"
                src={lightbox.items[lightbox.index]?.url}
                alt={lightbox.items[lightbox.index]?.label || "Photo"}
              />

              <button
                type="button"
                className="ink-lightbox-nav ink-lightbox-next"
                onClick={goNext}
                disabled={lightbox.items.length <= 1}
                aria-label="Next"
              >
                ›
              </button>
            </div>

            {lightbox.items[lightbox.index]?.label ? (
              <div className="ink-lightbox-caption">{lightbox.items[lightbox.index]?.label}</div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprenticeshipPanel;
